import { useGameCreator } from "@/contexts/GameCreatorContext";
import { useState, useRef, useEffect } from "react";
import { Send, Bot, User, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { MarkdownMessage } from "@/components/MarkdownMessage";

function TypingIndicator() {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="w-8 h-8 rounded-lg bg-neon/15 flex items-center justify-center shrink-0">
        <Bot className="w-4 h-4 text-neon" />
      </div>
      <div className="flex items-center gap-2 pt-2">
        <div className="flex gap-1.5">
          {[0, 1, 2].map(i => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full bg-neon/60"
              animate={{ opacity: [0.3, 1, 0.3], y: [0, -4, 0] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </div>
        <span className="text-xs text-muted-foreground/60 ml-1">
          Thinking...
        </span>
      </div>
    </div>
  );
}

function UrlBadge({ url }: { url: string }) {
  let displayUrl = url;
  try {
    const parsed = new URL(url);
    displayUrl =
      parsed.hostname +
      (parsed.pathname !== "/" ? parsed.pathname.slice(0, 30) : "");
  } catch {
    displayUrl = url.slice(0, 40);
  }

  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-neon/10 border border-neon/20 text-neon text-xs mt-1.5">
      <Globe className="w-3 h-3" />
      <span className="truncate max-w-[200px]">{displayUrl}</span>
      <span className="text-neon/50">— analyzing</span>
    </div>
  );
}

export default function ChatPanel() {
  const { messages, sendMessage, isTyping } = useGameCreator();
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isTyping) return;
    sendMessage(trimmed);
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  // Detect URLs in user messages for visual badge
  const hasUrl = (text: string) => /https?:\/\/[^\s]+/i.test(text);

  return (
    <div className="flex flex-col h-full">
      {/* Chat messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-1 pb-4">
        <AnimatePresence initial={false}>
          {messages.map(msg => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex items-start gap-3 px-4 py-3 ${
                msg.role === "user" ? "bg-secondary/30" : ""
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  msg.role === "assistant" ? "bg-neon/15" : "bg-gold/15"
                }`}
              >
                {msg.role === "assistant" ? (
                  <Bot className="w-4 h-4 text-neon" />
                ) : (
                  <User className="w-4 h-4 text-gold" />
                )}
              </div>
              <div className="flex-1 min-w-0 text-sm leading-relaxed">
                <div className="text-xs font-medium mb-1 text-muted-foreground">
                  {msg.role === "assistant" ? "GP Advisor" : "You"}
                </div>
                <div className="prose prose-invert prose-sm max-w-none [&_strong]:text-neon [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_table]:text-xs [&_td]:px-2 [&_td]:py-1 [&_th]:px-2 [&_th]:py-1 [&_th]:text-neon/80 [&_table]:border-border [&_td]:border-border [&_th]:border-border [&_li]:text-foreground/80">
                  <MarkdownMessage>{msg.content}</MarkdownMessage>
                </div>
                {/* Show URL badge for user messages containing URLs */}
                {msg.role === "user" && hasUrl(msg.content) && isTyping && (
                  <UrlBadge
                    url={msg.content.match(/https?:\/\/[^\s]+/i)?.[0] || ""}
                  />
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {isTyping && <TypingIndicator />}
      </div>

      {/* Suggested prompts for empty state */}
      {messages.length <= 1 && (
        <div className="px-4 pb-3">
          <div className="flex flex-wrap gap-2">
            {[
              "I want to create a game for a YouTube creator",
              "Help me design a game around a trending topic",
              "I'm a fitness influencer — what games work for my audience?",
            ].map(prompt => (
              <button
                key={prompt}
                onClick={() => {
                  setInput(prompt);
                  inputRef.current?.focus();
                }}
                className="text-xs px-3 py-1.5 rounded-full border border-border/50 text-muted-foreground hover:text-neon hover:border-neon/30 transition-colors"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-border p-3">
        <div className="flex items-end gap-2 glass-panel rounded-lg p-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder="Paste a creator's URL, describe your audience, or answer the advisor's question..."
            className="flex-1 bg-transparent resize-none text-sm text-foreground placeholder:text-muted-foreground focus:outline-none min-h-[40px] max-h-[120px] py-2 px-2"
            rows={1}
            disabled={isTyping}
          />
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            className="bg-neon hover:bg-neon-light text-background shrink-0 h-9 w-9 p-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground/50 mt-1.5 px-1">
          Press Enter to send, Shift+Enter for new line. Paste any URL to
          analyze a creator.
        </p>
      </div>
    </div>
  );
}
